import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LoaderIcon } from '@/config/icons';
import { cn } from '@/lib/utils';

const COLOR_OPTIONS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', 
  '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#64748b'
];

interface Category {
  id: string;
  name: string;
  color: string;
  position: number;
  ticket_count?: number;
}

interface TicketTag {
  id: string;
  name: string;
  color: string;
  position: number;
  ticket_count?: number;
}

interface CategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingCategory: Category | null;
  categoryName: string;
  setCategoryName: (name: string) => void;
  categoryColor: string;
  setCategoryColor: (color: string) => void;
  onSave: () => void;
  saving: boolean;
}

export function CategoryDialog({
  open,
  onOpenChange,
  editingCategory,
  categoryName,
  setCategoryName,
  categoryColor,
  setCategoryColor,
  onSave,
  saving,
}: CategoryDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingCategory ? 'Modifier le dossier' : 'Nouveau dossier'}
          </DialogTitle>
          <DialogDescription>
            {editingCategory 
              ? 'Modifiez le nom et la couleur du dossier'
              : 'Créez un nouveau dossier pour organiser vos tickets'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Nom du dossier</label>
            <Input
              placeholder="Ex: Support technique"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSave();
              }}
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Couleur</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color}
                  onClick={() => setCategoryColor(color)}
                  className={cn(
                    "w-8 h-8 rounded-full border-2 transition-all",
                    categoryColor === color 
                      ? "border-foreground scale-110" 
                      : "border-transparent hover:scale-105"
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={onSave} disabled={!categoryName.trim() || saving}>
            {saving && <LoaderIcon className="h-4 w-4 mr-2 animate-spin" />}
            {editingCategory ? 'Modifier' : 'Créer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface TagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingTag: TicketTag | null;
  tagName: string;
  setTagName: (name: string) => void;
  tagColor: string;
  setTagColor: (color: string) => void;
  onSave: () => void;
  saving: boolean;
}

export function TagDialog({
  open,
  onOpenChange,
  editingTag,
  tagName,
  setTagName,
  tagColor,
  setTagColor,
  onSave,
  saving,
}: TagDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingTag ? 'Modifier le tag' : 'Nouveau tag'}
          </DialogTitle>
          <DialogDescription>
            {editingTag 
              ? 'Modifiez le nom et la couleur du tag'
              : 'Créez un nouveau tag pour catégoriser vos tickets'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Nom du tag</label>
            <Input
              placeholder="Ex: Urgent, VIP, Bug..."
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSave();
              }}
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Couleur</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color}
                  onClick={() => setTagColor(color)}
                  className={cn(
                    "w-8 h-8 rounded-full border-2 transition-all",
                    tagColor === color 
                      ? "border-foreground scale-110" 
                      : "border-transparent hover:scale-105"
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={onSave} disabled={!tagName.trim() || saving}>
            {saving && <LoaderIcon className="h-4 w-4 mr-2 animate-spin" />}
            {editingTag ? 'Modifier' : 'Créer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DeleteCategoryDialogProps {
  category: Category | null;
  onOpenChange: (category: Category | null) => void;
  onDelete: () => void;
}

export function DeleteCategoryDialog({
  category,
  onOpenChange,
  onDelete,
}: DeleteCategoryDialogProps) {
  return (
    <AlertDialog open={!!category} onOpenChange={(open) => !open && onOpenChange(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer le dossier ?</AlertDialogTitle>
          <AlertDialogDescription>
            Le dossier "{category?.name}" sera supprimé. Les tickets associés ne seront pas supprimés mais perdront leur catégorie.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onOpenChange(null)}>
            Annuler
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Supprimer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface DeleteTagDialogProps {
  tag: TicketTag | null;
  onOpenChange: (tag: TicketTag | null) => void;
  onDelete: () => void;
}

export function DeleteTagDialog({
  tag,
  onOpenChange,
  onDelete,
}: DeleteTagDialogProps) {
  return (
    <AlertDialog open={!!tag} onOpenChange={(open) => !open && onOpenChange(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer le tag ?</AlertDialogTitle>
          <AlertDialogDescription>
            Le tag "{tag?.name}" sera supprimé de tous les tickets.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onOpenChange(null)}>
            Annuler
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Supprimer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export { COLOR_OPTIONS };
export type { Category, TicketTag };
